import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { DollarSign, TrendingDown, TrendingUp, AlertTriangle, BarChart2, Wallet } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import KpiCard from "@/components/KpiCard";
import ChartCard from "@/components/ChartCard";
import DateRangePicker, { rangeToQueryString } from "@/components/DateRangePicker";
import { api, formatCompactMoney, formatMoney } from "@/lib/api";
import { toast, Toaster } from "sonner";

const PIE_COLORS = ["#0B1A30", "#F28C24", "#475569", "#1E3A5F", "#F4A261", "#8A95A5", "#D97706", "#475880"];

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ months: 12 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = rangeToQueryString(range);
    api
      .get(`/dashboard/executive?${qs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, [range]);

  const kpis = data?.kpis ?? {};

  const budgetSpend = useMemo(() => {
    return (data?.budget_vs_spend ?? []).map((d) => ({
      month: d.month.slice(2),
      Budget: Math.round(d.budget),
      Spend: Math.round(d.spend),
    }));
  }, [data]);

  const goto = (path) => navigate(path);
  const gotoRecords = (type) => navigate(`/records?type=${type}`);
  const gotoCompany = (companyId) => {
    if (companyId) navigate(`/companies/${companyId}`);
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Executive Dashboard"
        subtitle="Enterprise financial health · Unified SSOT"
        right={<DateRangePicker value={range} onChange={setRange} testId="exec-range" />}
      />

      <div className="space-y-6 p-6 animate-fade-in" data-testid="executive-dashboard">
        {/* KPI Row — clickable for drill-down */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
          <DrillKpi onClick={() => gotoRecords("REVENUE")}>
            <KpiCard
              testId="kpi-revenue"
              tone="navy"
              label="Total Revenue"
              value={kpis.total_revenue}
              icon={TrendingUp}
              money
              trend={12.4}
            />
          </DrillKpi>
          <DrillKpi onClick={() => gotoRecords("SPEND")}>
            <KpiCard
              testId="kpi-spend"
              label="Operating Spend"
              value={kpis.total_spend}
              icon={Wallet}
              money
              trend={-3.1}
            />
          </DrillKpi>
          <DrillKpi onClick={() => goto("/vc-reporting")}>
            <KpiCard
              testId="kpi-ebitda"
              label="EBITDA"
              value={kpis.ebitda}
              icon={BarChart2}
              money
              trend={8.7}
            />
          </DrillKpi>
          <DrillKpi onClick={() => gotoRecords("BUDGET")}>
            <KpiCard
              testId="kpi-budget-util"
              label="Budget utilisation"
              value={kpis.budget_utilization_pct}
              icon={DollarSign}
              suffix="%"
              trend={-1.2}
            />
          </DrillKpi>
          <DrillKpi onClick={() => goto("/fines")}>
            <KpiCard
              testId="kpi-fines"
              label="Fines YTD"
              value={kpis.total_fines}
              icon={AlertTriangle}
              money
              trend={4.3}
              tone="light"
            />
          </DrillKpi>
          <DrillKpi onClick={() => goto("/vc-reporting")}>
            <KpiCard
              testId="kpi-runway"
              label="Cash runway"
              value={kpis.cash_runway_months}
              icon={TrendingDown}
              suffix="mo"
              trend={2.1}
            />
          </DrillKpi>
        </div>

        {/* Main charts */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <ChartCard
            testId="chart-budget-vs-spend"
            title="Budget vs. Spend"
            subtitle="Monthly aggregated across all companies"
            className="xl:col-span-2"
          >
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={budgetSpend} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fill: "#475569", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
                  <YAxis tick={{ fill: "#475569", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => formatCompactMoney(v)} />
                  <Tooltip
                    cursor={{ fill: "rgba(11, 26, 48, 0.04)" }}
                    contentStyle={{ background: "#0B1A30", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(value) => formatMoney(value)}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#475569" }} />
                  <Bar dataKey="Budget" fill="#0B1A30" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Spend" fill="#F28C24" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard
            testId="chart-it-spend-by-company"
            title="IT Spend by Company"
            subtitle="Click a slice to drill into a company"
          >
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data?.it_spend_by_company ?? []}
                    dataKey="amount"
                    nameKey="company"
                    innerRadius={50}
                    outerRadius={100}
                    paddingAngle={2}
                    onClick={(d) => gotoCompany(d?.companyId)}
                    cursor="pointer"
                  >
                    {(data?.it_spend_by_company ?? []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#0B1A30", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }}
                    formatter={(value) => formatMoney(value)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 max-h-32 space-y-1.5 overflow-y-auto text-xs">
              {(data?.it_spend_by_company ?? []).slice(0, 6).map((row, i) => (
                <button
                  key={row.company}
                  type="button"
                  onClick={() => gotoCompany(row.companyId)}
                  data-testid={`it-spend-row-${i}`}
                  className="flex w-full items-center justify-between rounded-sm px-1.5 py-1 text-left transition-colors hover:bg-slate-50"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="h-2 w-2 shrink-0 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="truncate text-slate-600 group-hover:text-navy-600">{row.company}</span>
                  </div>
                  <span className="tabular font-medium text-navy-600">{formatCompactMoney(row.amount)}</span>
                </button>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* Bottom row: Spend by Category + Fines table */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <ChartCard
            testId="chart-spend-by-category"
            title="Spend by Category"
            subtitle="Enterprise-wide · click a bar to filter records"
          >
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.spend_by_category ?? []}
                  layout="vertical"
                  margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
                  onClick={(st) => {
                    const cat = st?.activePayload?.[0]?.payload?.category;
                    if (cat) navigate(`/records?type=SPEND&search=${encodeURIComponent(cat)}`);
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fill: "#475569", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => formatCompactMoney(v)} />
                  <YAxis type="category" dataKey="category" tick={{ fill: "#475569", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} width={80} />
                  <Tooltip
                    cursor={{ fill: "rgba(11, 26, 48, 0.04)" }}
                    contentStyle={{ background: "#0B1A30", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }}
                    formatter={(value) => formatMoney(value)}
                  />
                  <Bar dataKey="amount" fill="#F28C24" radius={[0, 3, 3, 0]} cursor="pointer" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard
            testId="recent-fines-card"
            title="Recent Fines"
            subtitle="Click a row to open that company"
            className="xl:col-span-2"
          >
            <div className="overflow-hidden rounded-sm border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-2.5 text-left">Company</th>
                    <th className="px-4 py-2.5 text-left">Type</th>
                    <th className="px-4 py-2.5 text-left">Date</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recent_fines ?? []).length === 0 && !loading && (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-slate-400">
                        No fines for the selected period.
                      </td>
                    </tr>
                  )}
                  {(data?.recent_fines ?? []).map((f) => (
                    <tr
                      key={f.id}
                      onClick={() => gotoCompany(f.companyId)}
                      className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      data-testid={`fine-row-${f.id}`}
                    >
                      <td className="px-4 py-2.5 font-medium text-navy-600">{f.companyName}</td>
                      <td className="px-4 py-2.5 text-slate-600">{f.fine_type}</td>
                      <td className="px-4 py-2.5 text-slate-500 tabular">
                        {new Date(f.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium tabular text-brand-orange">
                        {formatMoney(f.amount, f.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        </div>

        {loading && <div className="text-center text-xs text-slate-500">Loading data…</div>}
      </div>
    </>
  );
}

function DrillKpi({ onClick, children }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
      className="cursor-pointer transition-transform hover:-translate-y-0.5"
    >
      {children}
    </div>
  );
}
